/**
 * The patient ward: every subject of the study stands as a life-size mannequin on a plinth
 * in a tiered gallery beyond the dashboard arc. Bodies are baked static geometry drawn
 * through six instanced meshes (two bodies, three LODs); plinths, status rings, floor
 * labels, visit spines, body overlays and the walkable timeline are instanced or merged too,
 * so the whole ward is about sixteen draw calls whatever the study size. Hover gives a
 * name-tag card, select opens the profile (through the room), grip pulls a mannequin out of
 * the formation to stand in front of the viewer with its complete record beside it, and
 * every spine node, overlay and timeline disc opens the record it stands for.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  BMI_BUCKETS,
  SUBJECT_FLAGS,
  SUBJECT_STATUSES,
  type DashboardFilters,
  type Subject,
  type Visit,
} from '@clinspace/shared';
import {
  PALETTE,
  chip,
  fillRoundRect,
  fitSize,
  font,
  text,
  truncate,
} from '../dashboards/charts.ts';
import { CanvasPanel, type Region } from '../dashboards/CanvasPanel.ts';
import type { StudyView } from '../dashboards/StudyView.ts';
import { Tooltip } from '../dashboards/Tooltip.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';
import type { InputManager, Pointer } from '../xr/InputManager.ts';
import type { AvatarLoader } from '../world/avatars.ts';
import { bakeBody, mannequinMaterial, withPhases, type MannequinMaterial } from './mannequins.ts';
import { clampToPolygon } from '../xr/Locomotion.ts';
import { RecordSheet } from './RecordSheet.ts';
import { SwimmerPlot } from './SwimmerPlot.ts';
import { WARD_HINT, buildWardHint } from './WardHint.ts';

/** The filter console's size in metres (the how-to tab sits on its top-left edge). */
const CONSOLE_SIZE = { width: 1.5, height: 0.75 };
import {
  ANALYTE_ORGAN,
  ORGAN_ANCHOR,
  REGION_ANCHOR,
  SPINE,
  STATUS_COLOUR,
  TIMELINE,
  WARD,
  AGE_BANDS,
  bodyScale,
  forwardOf,
  gradeColour,
  layoutWard,
  nearestTimelineNode,
  walkableOutline,
  wardRoom,
  reviewArrangement,
  placeCockpit,
  REVIEW,
  rightOf,
  wardPoint,
  ringColour,
  skinTone,
  spineBase,
  spineNodes,
  timelineNodes,
  toSlot,
  type Organ,
  type ReviewArrangement,
  type SpineNode,
  type TimelineNode,
  type WardSlot,
} from './layout.ts';

export type WardTarget =
  | { kind: 'subject'; subjectId: string }
  | { kind: 'visit'; subjectId: string; visitId: string }
  | { kind: 'lab'; subjectId: string; labId: string; analyte: string }
  | { kind: 'ae'; subjectId: string; aeId: string }
  | { kind: 'ecg'; subjectId: string; ecgId: string }
  | { kind: 'record'; subjectId: string; path: string[] };

export type OverlayMode = 'off' | 'labs' | 'aes' | 'both';
const OVERLAY_CYCLE: OverlayMode[] = ['off', 'labs', 'aes', 'both'];

export interface WardHandlers {
  tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
  select(target: WardTarget, at: THREE.Vector3): void;
  /** Grip (or G on desktop) on a mannequin: pull it out for review, or send it back. */
  grab(subjectId: string): void;
  /** Every summoned patient back to the formation (the console button). */
  sendBackAll(): void;
  /** A summoned patient's cockpit moved (dragged): its profile panel follows. */
  reviewMoved(subjectId: string): void;
  /** A patient filter chosen at the ward console (applied to the whole room). */
  filter(patch: Partial<DashboardFilters>): void;
  /** The viewer stepped onto a timeline disc (or off every disc: undefined). */
  timelineStep(subjectId: string, visitId: string | undefined): void;
}

export interface Miniature {
  id: string;
  subjectId: string;
  position: THREE.Vector3;
  yaw: number;
}

type Sex = 'M' | 'F';
type Lod = 'lod0' | 'lod1' | 'lod2';
const LODS: Lod[] = ['lod0', 'lod1', 'lod2'];
const LOD_CAP: Record<Lod, number> = { lod0: 4, lod1: 64, lod2: 80 };
/** Within this distance of the viewer a mannequin uses the mid LOD; profiles open get lod0. */
const NEAR_M = 7;
const LOD_INTERVAL_MS = 250;
const MINI_SCALE = 0.22;
const PLINTH_COLOUR = '#5e564e';
const PLINTH_OPEN = '#3fd6ff';
const PLINTH_EMPTY = '#3f3934';

const CAP = { subjects: 64, nodes: 1024, hollow: 512, organs: 320, aes: 512, discs: 32 };

/* Floor label atlas: a cell per slot plus cells for the timeline discs. */
const CELL_W = 240;
const CELL_H = 72;
const ATLAS_SCALE = 1.5;
const ATLAS_COLS = 8;
const SUBJECT_ROWS = 8;
const TIMELINE_ROWS = 4;
const LABEL_W = 0.5;
const LABEL_H = 0.15;
/* Chest numbers: one atlas cell per subject of the study, below the floor-label rows. */
const CHEST_CELL = { w: 200, h: 50 };
const CHEST_ROWS = 8;
const CHEST_Y0 = CELL_H * (SUBJECT_ROWS + TIMELINE_ROWS);
const ATLAS_H = CHEST_Y0 + CHEST_CELL.h * CHEST_ROWS;
const CHEST_W = 0.19;
const CHEST_H = (CHEST_W * CHEST_CELL.h) / CHEST_CELL.w;
/** Chest height and how far in front of the body axis the number floats, per metre of height. */
const CHEST_AT = { y: 0.72, depth: 0.085, gap: 0.02 };
/** Two grips this close together on a pulled-out mannequin send it back. */
const DOUBLE_GRIP_MS = 450;

interface OrganHit {
  kind: 'lab' | 'ecg';
  id: string;
  analyte: string;
  value: string;
  grade: number;
  visitCode: string;
  date: string;
}

interface OverlayEntry {
  target: WardTarget;
  tooltip: string[];
}

interface BodyEntry {
  subjectId: string;
  /** Miniatures carry the pin id; ward instances none. */
  miniature?: string;
}

/** A summoned patient's cockpit, as the room shares it: the anchor's head and facing. */
export interface ReviewAnchor {
  subjectId: string;
  head: [number, number, number];
  forward: [number, number];
}

export class Ward {
  readonly group = new THREE.Group();
  /** Formation slots of the subjects currently shown (room filters re-pack the formation). */
  slots: WardSlot[] = [];
  private readonly slotById = new Map<string, WardSlot>();
  private readonly subjectById = new Map<string, Subject>();
  private readonly bodies: Partial<Record<`${Sex}-${Lod}`, THREE.InstancedMesh>> = {};
  private readonly bodyEntries = new Map<THREE.InstancedMesh, BodyEntry[]>();
  private readonly material: MannequinMaterial;
  private readonly plinths: THREE.InstancedMesh;
  private readonly rings: THREE.InstancedMesh;
  private readonly rods: THREE.InstancedMesh;
  private readonly nodes: THREE.InstancedMesh;
  private readonly hollow: THREE.InstancedMesh;
  private readonly organs: THREE.InstancedMesh;
  private readonly aeMarkers: THREE.InstancedMesh;
  private readonly discs: THREE.InstancedMesh;
  private readonly atlas = document.createElement('canvas');
  private readonly atlasTexture: THREE.CanvasTexture;
  private readonly labelMaterial: THREE.MeshBasicMaterial;
  private readonly labelMesh: THREE.Mesh;
  private readonly discLabels: THREE.Mesh;
  private readonly console: CanvasPanel;
  /** Record sheets and swimmer plots, one pair per cockpit, reused as patients come and go. */
  private readonly cockpitPool: { sheet: RecordSheet; swimmer: SwimmerPlot }[] = [];
  private readonly stepTip = new Tooltip();
  private readonly nodeEntries: OverlayEntry[] = [];
  private readonly hollowEntries: OverlayEntry[] = [];
  private readonly organEntries: OverlayEntry[] = [];
  private readonly aeEntries: OverlayEntry[] = [];
  private discEntries: (OverlayEntry & { node: TimelineNode })[] = [];
  /** Subject behind each plinth instance (formation, then the review plinth, then minis). */
  private plinthSubjects: (string | undefined)[] = [];
  private readonly pulsing: number[] = [];
  private miniatures: Miniature[] = [];
  private filters: DashboardFilters = {};
  private open = new Set<string>();
  private lods = new Map<string, Lod>();
  private lastLodAt = 0;
  private hovered: { mesh: THREE.InstancedMesh; index: number; colour: THREE.Color } | undefined;
  /** Which mannequin each pointer rests on, for the grip gesture. */
  private readonly hoverSubject = new Map<string, string>();
  private readonly wasSqueezing = new Map<string, boolean>();
  /** The subject number on every standing mannequin's chest. */
  private readonly chestMesh: THREE.Mesh;
  private readonly chestIndex = new Map<string, number>();
  /** How many chest numbers are showing (dev hook). */
  chestCount = 0;
  /** A pulled-out mannequin being dragged with a held grip. */
  private drag: { pointerId: string; distance: number; subjectId: string } | undefined;
  private lastGrip: { subjectId: string; at: number } | undefined;
  private lastDragBuild = 0;
  private readonly lastHead = new THREE.Vector3(WARD.focal.x, 1.6, WARD.focal.z);
  private timelineSubject: string | undefined;
  private timelineNodesList: TimelineNode[] = [];
  private lastStep: string | undefined;
  /** Mannequins summoned out of the formation, each standing in its own review cockpit. */
  /** The how-to sign beside the console (see WardHint.ts). */
  private readonly hint = buildWardHint();
  private readonly reviews: {
    subjectId: string;
    arrangement: ReviewArrangement;
    /** The cockpit's anchor: where its viewer stands and faces. Moves with a drag. */
    head: THREE.Vector3;
    forward: THREE.Vector3;
    pages: number;
    sheet: RecordSheet;
    swimmer: SwimmerPlot;
  }[] = [];
  private time = 0;
  /** Lab and AE overlays are on from the start (the user's choice, 2026-10-03). */
  overlays: OverlayMode = 'both';
  /** True once every body has loaded and baked. */
  ready = false;
  loadError: string | undefined;
  /** The floor a pulled-out patient may be dragged over. */
  private readonly floor: { x: number; z: number }[];
  /** Triangles per LOD per body, for the budget note. */
  readonly triangles: Partial<Record<`${Sex}-${Lod}`, number>> = {};

  constructor(
    private readonly view: StudyView,
    private readonly interactive: InteractiveSystem,
    private readonly input: InputManager,
    loader: AvatarLoader,
    private readonly handlers: WardHandlers,
  ) {
    this.group.name = 'ward';
    for (const s of view.file.subjects) this.subjectById.set(s.id, s);
    this.floor = walkableOutline(wardRoom(view.file.subjects.length));
    this.material = mannequinMaterial();

    const solid = (extra: Partial<THREE.MeshStandardMaterialParameters> = {}) =>
      new THREE.MeshStandardMaterial({ roughness: 0.6, metalness: 0, ...extra });
    // Small coloured markers are unlit so their colours stay saturated from any angle.
    const flat = (extra: Partial<THREE.MeshBasicMaterialParameters> = {}) =>
      new THREE.MeshBasicMaterial({ toneMapped: false, ...extra });
    const make = (
      geometry: THREE.BufferGeometry,
      material: THREE.Material,
      cap: number,
      name: string,
    ) => {
      const m = new THREE.InstancedMesh(geometry, material, cap);
      m.count = 0;
      m.name = name;
      return m;
    };
    // The plinth cylinder is one metre tall; tiers scale it in y.
    this.plinths = make(
      new THREE.CylinderGeometry(WARD.plinthRadius, WARD.plinthRadius + 0.03, 1, 28),
      solid({ roughness: 0.75 }),
      CAP.subjects + 10,
      'ward-plinths',
    );
    this.rings = make(
      new THREE.TorusGeometry(WARD.plinthRadius + 0.035, 0.014, 5, 32),
      flat(),
      CAP.subjects + 2,
      'ward-rings',
    );
    this.rods = make(
      new THREE.CylinderGeometry(0.006, 0.006, SPINE.yMax - SPINE.yMin + 0.1, 6),
      new THREE.MeshBasicMaterial({ color: 0xa9b4c2, transparent: true, opacity: 0.35 }),
      CAP.subjects + 2,
      'ward-spines',
    );
    this.nodes = make(new THREE.IcosahedronGeometry(1, 0), flat(), CAP.nodes, 'ward-spine-nodes');
    this.hollow = make(
      new THREE.TorusGeometry(SPINE.radius, 0.008, 5, 16),
      flat(),
      CAP.hollow,
      'ward-spine-missed',
    );
    this.organs = make(
      new THREE.SphereGeometry(1, 8, 6),
      flat({ transparent: true, opacity: 0.8, depthWrite: false }),
      CAP.organs,
      'ward-organs',
    );
    this.aeMarkers = make(new THREE.OctahedronGeometry(1, 0), flat(), CAP.aes, 'ward-ae-markers');
    this.discs = make(
      new THREE.CylinderGeometry(TIMELINE.discRadius, TIMELINE.discRadius, 0.014, 24),
      flat(),
      CAP.discs,
      'ward-timeline',
    );

    this.atlas.width = CELL_W * ATLAS_COLS * ATLAS_SCALE;
    this.atlas.height = ATLAS_H * ATLAS_SCALE;
    this.atlasTexture = new THREE.CanvasTexture(this.atlas);
    this.atlasTexture.colorSpace = THREE.SRGBColorSpace;
    this.atlasTexture.anisotropy = 8;
    this.labelMaterial = new THREE.MeshBasicMaterial({
      map: this.atlasTexture,
      transparent: true,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    this.labelMesh = new THREE.Mesh(new THREE.BufferGeometry(), this.labelMaterial);
    this.labelMesh.name = 'ward-labels';
    this.discLabels = new THREE.Mesh(new THREE.BufferGeometry(), this.labelMaterial);
    this.discLabels.name = 'ward-timeline-labels';
    this.chestMesh = new THREE.Mesh(this.buildChestGeometry(), this.labelMaterial);
    this.chestMesh.name = 'ward-chest-numbers';

    // Patient filters, legend and controls: a low desk in front of the ward, between the
    // viewer on the focal ring and the first row, tilted back so it reads from standing height
    // and the ward shows over it.
    this.console = new CanvasPanel({
      width: CONSOLE_SIZE.width,
      height: CONSOLE_SIZE.height,
      pixelWidth: 1300,
      pixelHeight: 650,
      name: 'ward-console',
      resolution: 1.5,
    });
    const desk = wardPoint(0, WARD.consoleDistance);
    this.console.mesh.position.set(desk.x, 0.75, desk.z);
    this.console.mesh.quaternion.setFromEuler(
      new THREE.Euler(-Math.PI / 3, Math.atan2(-WARD.forward.x, -WARD.forward.z), 0, 'YXZ'),
    );
    this.console.setDraw((ctx, panel) => this.drawConsole(ctx, panel));
    this.console.onSelect = (region) => this.consoleSelect(region);
    this.console.onHoverChange = (region, point) =>
      handlers.tooltip(region?.tooltip, region ? point : undefined);
    this.console.attach(interactive);
    this.stepTip.sprite.scale.set(0.9, 0.28, 1);
    // The how-to card is a tab on the console's top-left edge, in the console's plane and
    // tilt: part of the console, below the patients (an eye-level sign hid the front row).
    this.hint.position.set(
      -CONSOLE_SIZE.width / 2 + WARD_HINT.width / 2,
      CONSOLE_SIZE.height / 2 + WARD_HINT.height / 2 + 0.01,
      0.001,
    );
    this.console.mesh.add(this.hint);

    this.group.add(
      this.plinths,
      this.rings,
      this.rods,
      this.nodes,
      this.hollow,
      this.organs,
      this.aeMarkers,
      this.discs,
      this.labelMesh,
      this.discLabels,
      this.chestMesh,
      this.console.mesh,
      this.stepTip.sprite,
    );
    this.registerInstanced(this.plinths, (i) => {
      const id = this.plinthSubjects[i];
      return id
        ? { target: { kind: 'subject', subjectId: id }, tooltip: this.nameTag(id) }
        : undefined;
    });
    this.registerInstanced(this.nodes, (i) => this.nodeEntries[i]);
    this.registerInstanced(this.hollow, (i) => this.hollowEntries[i]);
    this.registerInstanced(this.organs, (i) => this.organEntries[i]);
    this.registerInstanced(this.aeMarkers, (i) => this.aeEntries[i]);
    this.registerInstanced(this.discs, (i) => this.discEntries[i]);

    this.relayout();
    void this.load(loader);
  }

  /* ---------- Loading and baking ---------- */

  private async load(loader: AvatarLoader): Promise<void> {
    try {
      const clips = await loader.loadAnimations();
      const idle = clips.find((c) => c.name === 'idle') ?? clips[0];
      for (const sex of ['M', 'F'] as Sex[]) {
        for (const lod of LODS) {
          const root = await loader.loadMannequinBody(sex === 'M' ? 'male' : 'female', lod);
          const baked = bakeBody(root, idle, 0.6);
          const cap = LOD_CAP[lod];
          const mesh = new THREE.InstancedMesh(withPhases(baked.geometry, cap), this.material, cap);
          mesh.count = 0;
          mesh.name = `ward-body-${sex}-${lod}`;
          this.bodies[`${sex}-${lod}`] = mesh;
          this.triangles[`${sex}-${lod}`] = baked.triangles;
          this.bodyEntries.set(mesh, []);
          this.group.add(mesh);
          this.registerInstanced(mesh, (i) => {
            const e = this.bodyEntries.get(mesh)?.[i];
            return e
              ? {
                  target: { kind: 'subject', subjectId: e.subjectId },
                  tooltip: this.nameTag(e.subjectId),
                }
              : undefined;
          });
        }
      }
      this.ready = true;
      this.assignLods(new THREE.Vector3(WARD.focal.x, 1.6, WARD.focal.z), true);
    } catch (err) {
      this.loadError = err instanceof Error ? err.message : String(err);
      console.error(`[ward] mannequins failed to load: ${this.loadError}`);
    }
  }

  private registerInstanced(
    mesh: THREE.InstancedMesh,
    entry: (index: number) => OverlayEntry | undefined,
  ): void {
    const colour = new THREE.Color();
    this.interactive.add({
      object: mesh,
      onHover: (p, point, hit) => {
        const i = hit?.instanceId ?? -1;
        const e = entry(i);
        if (e?.target.kind === 'subject') this.hoverSubject.set(p.id, e.target.subjectId);
        else this.hoverSubject.delete(p.id);
        if (this.hovered?.mesh === mesh && this.hovered.index === i) return;
        this.unhover();
        if (!e) return;
        mesh.getColorAt(i, colour);
        this.hovered = { mesh, index: i, colour: colour.clone() };
        mesh.setColorAt(i, colour.lerp(new THREE.Color(0xffffff), 0.35));
        mesh.instanceColor!.needsUpdate = true;
        this.handlers.tooltip(e.tooltip, point);
      },
      onLeave: (p) => {
        this.hoverSubject.delete(p.id);
        this.unhover();
        this.handlers.tooltip(undefined, undefined);
      },
      onSelect: (_p, point, hit) => {
        const e = entry(hit?.instanceId ?? -1);
        if (e) this.handlers.select(e.target, point);
      },
    });
  }

  private unhover(): void {
    const h = this.hovered;
    if (!h) return;
    h.mesh.setColorAt(h.index, h.colour);
    if (h.mesh.instanceColor) h.mesh.instanceColor.needsUpdate = true;
    this.hovered = undefined;
  }

  /** The meshes rays can hit (for tests). */
  get hitObjects(): THREE.Object3D[] {
    return [
      ...Object.values(this.bodies).filter((m): m is THREE.InstancedMesh => !!m),
      this.plinths,
      this.nodes,
      this.hollow,
      this.organs,
      this.aeMarkers,
      this.discs,
    ];
  }

  /* ---------- Name tag, placement helpers ---------- */

  /** The hover card: id, age, sex, site, arm, enrollment, status, open queries. */
  nameTag(subjectId: string): string[] {
    const s = this.subjectById.get(subjectId);
    if (!s) return [subjectId];
    const site = this.view.site(s.siteId);
    const pulled = this.isPulled(subjectId);
    const queries = this.view.openQueryCount(s.id);
    return [
      `${s.id} · ${s.sex === 'F' ? 'female' : 'male'} ${s.age}`,
      `Site ${s.siteId}${site ? ` ${site.city}` : ''} · ${s.arm ?? 'not randomised'}`,
      `${s.status} · consented ${s.consentDate}`,
      pulled
        ? `${queries} open queries · hold grip to drag · grip twice to send back`
        : `${queries} open queries · select: profile · grip: pull out`,
    ];
  }

  /** The formation slot of a subject (undefined while filtered out of the ward). */
  slot(subjectId: string): WardSlot | undefined {
    return this.slotById.get(subjectId);
  }

  /** Where the mannequin stands: its review slot while pulled out, else its formation slot. */
  private effectiveSlot(subjectId: string): WardSlot | undefined {
    return this.reviewOf(subjectId)?.arrangement.slot ?? this.slotById.get(subjectId);
  }

  /** The mannequin's chest, for dev hooks and markers. */
  slotWorld(subjectId: string, target = new THREE.Vector3()): THREE.Vector3 | undefined {
    const slot = this.effectiveSlot(subjectId);
    const s = this.subjectById.get(subjectId);
    if (!slot || !s) return undefined;
    return target.set(slot.x, slot.y + WARD.plinthHeight + (s.heightCm / 100) * 0.7, slot.z);
  }

  /** Where to stand to face the mannequin, and what to look at. */
  standingPoint(subjectId: string): { position: THREE.Vector3; lookAt: THREE.Vector3 } | undefined {
    const slot = this.effectiveSlot(subjectId);
    if (!slot) return undefined;
    const review = this.reviewOf(subjectId);
    if (review)
      return {
        position: new THREE.Vector3(review.head.x, 0, review.head.z),
        lookAt: new THREE.Vector3(slot.x, 1.5, slot.z),
      };
    // Every mannequin faces the focal point, so that is where to stand.
    return {
      position: new THREE.Vector3(WARD.focal.x, 0, WARD.focal.z),
      lookAt: new THREE.Vector3(slot.x, 1.5 + slot.y, slot.z),
    };
  }

  /**
   * Pose for the profile panel: on the viewer's left beside a pulled-out mannequin,
   * otherwise ahead of the viewer toward the mannequin and to their left, facing them.
   */
  profilePose(
    subjectId: string,
    head: THREE.Vector3,
  ): { position: THREE.Vector3; quaternion: THREE.Quaternion; scale: number } | undefined {
    const slot = this.effectiveSlot(subjectId);
    if (!slot) return undefined;
    let position: THREE.Vector3;
    let scale = 0.62;
    let pitch = 0.1;
    const review = this.reviewOf(subjectId);
    if (review) {
      const p = review.arrangement.profile;
      position = new THREE.Vector3(p.x, p.y, p.z);
      scale = p.scale;
      // Above eye height: tilted a little more toward the viewer.
      pitch = 0.25;
    } else {
      const right = rightOf(slot, head);
      const dx = slot.x - head.x;
      const dz = slot.z - head.z;
      const len = Math.hypot(dx, dz) || 1;
      position = new THREE.Vector3(
        head.x + (dx / len) * 1.3 - right.x * 0.85,
        1.6,
        head.z + (dz / len) * 1.3 - right.z * 0.85,
      );
    }
    const yaw = Math.atan2(head.x - position.x, head.z - position.z);
    const quaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, yaw, 0, 'YXZ'));
    return { position, quaternion, scale };
  }

  /* ---------- Pull out for review ---------- */

  /** The most recently summoned patient. */
  get pulled(): string | undefined {
    return this.reviews.at(-1)?.subjectId;
  }

  /** Every summoned patient, oldest first. */
  get pulledIds(): string[] {
    return this.reviews.map((r) => r.subjectId);
  }

  isPulled(subjectId: string): boolean {
    return this.reviews.some((r) => r.subjectId === subjectId);
  }

  private reviewOf(subjectId: string | undefined) {
    return this.reviews.find((r) => r.subjectId === subjectId);
  }

  /** Where the most recently summoned mannequin stands now (dev hooks). */
  get pulledSlot(): WardSlot | undefined {
    return this.reviews.at(-1)?.arrangement.slot;
  }

  /** The record sheet of the most recent cockpit (or an idle one): dev hooks and tests. */
  get sheet(): RecordSheet {
    return (this.reviews.at(-1) ?? this.cockpit()).sheet;
  }

  get swimmer(): SwimmerPlot {
    return (this.reviews.at(-1) ?? this.cockpit()).swimmer;
  }

  /** A free sheet and swimmer plot, made on first use. */
  private cockpit(): { sheet: RecordSheet; swimmer: SwimmerPlot } {
    const used = new Set(this.reviews.map((r) => r.sheet));
    const free = this.cockpitPool.find((c) => !used.has(c.sheet));
    if (free) return free;
    const sheet: RecordSheet = new RecordSheet(this.view, this.interactive, {
      open: (subjectId, path) =>
        this.handlers.select({ kind: 'record', subjectId, path }, sheet.mesh.position),
      tooltip: (lines, at) => this.handlers.tooltip(lines, at),
    });
    const swimmer: SwimmerPlot = new SwimmerPlot(this.view, this.interactive, {
      open: (subjectId, path) =>
        this.handlers.select({ kind: 'record', subjectId, path }, swimmer.mesh.position),
      tooltip: (lines, at) => this.handlers.tooltip(lines, at),
    });
    this.group.add(sheet.group, swimmer.mesh);
    const c = { sheet, swimmer };
    this.cockpitPool.push(c);
    return c;
  }

  /**
   * The mannequin leaves its plinth and stands in front of the viewer in its own cockpit:
   * record pages to the right, swimmer plot and profile to the left. With others already out,
   * the new cockpit turns around the viewer (or steps aside) so none overlap. At most
   * REVIEW.maxPatients at once; returns false beyond that.
   */
  pullOut(subjectId: string, head: THREE.Vector3, forward: THREE.Vector3): boolean {
    if (!this.subjectById.has(subjectId)) return false;
    if (this.isPulled(subjectId)) return true;
    if (this.reviews.length >= REVIEW.maxPatients) return false;
    const { sheet, swimmer } = this.cockpit();
    const pages = sheet.load(subjectId);
    const anchor = placeCockpit(
      this.reviews.map((r) => ({ head: r.head, forward: r.forward, pages: r.pages })),
      head,
      forward,
      pages,
    );
    const arrangement = reviewArrangement(subjectId, anchor.head, anchor.forward, pages);
    this.reviews.push({
      subjectId,
      arrangement,
      head: new THREE.Vector3(anchor.head.x, head.y, anchor.head.z),
      forward: new THREE.Vector3(anchor.forward.x, 0, anchor.forward.z),
      pages,
      sheet,
      swimmer,
    });
    sheet.place(arrangement.sheets);
    swimmer.show(subjectId, arrangement.swimmer);
    this.rebuildStatics();
    this.assignLods(head, true);
    this.console.invalidate();
    return true;
  }

  /** The summoned patients and where their cockpits stand (shared with the room). */
  reviewAnchors(): ReviewAnchor[] {
    const r3 = (v: number) => Math.round(v * 1000) / 1000;
    return this.reviews.map((r) => ({
      subjectId: r.subjectId,
      head: [r3(r.head.x), r3(r.head.y), r3(r.head.z)],
      forward: [r3(r.forward.x), r3(r.forward.z)],
    }));
  }

  /**
   * Someone else's review state: patients they summoned stand in cockpits exactly where
   * theirs do, ones they sent back return to the formation, and moved cockpits move.
   * Returns the subjects that came out and went back.
   */
  setReviews(list: ReviewAnchor[]): { out: string[]; back: string[] } {
    const wanted = new Map(list.map((a) => [a.subjectId, a]));
    const back = this.reviews.filter((r) => !wanted.has(r.subjectId)).map((r) => r.subjectId);
    for (const id of back) this.sendBack(id);
    const out: string[] = [];
    for (const a of list.slice(0, REVIEW.maxPatients)) {
      if (!this.subjectById.has(a.subjectId)) continue;
      const head = new THREE.Vector3(a.head[0], a.head[1], a.head[2]);
      const forward = new THREE.Vector3(a.forward[0], 0, a.forward[1]);
      const r = this.reviewOf(a.subjectId);
      if (r) {
        if (r.head.distanceTo(head) < 0.002 && r.forward.distanceTo(forward) < 0.002) continue;
        r.head.copy(head);
        r.forward.copy(forward);
        r.arrangement = reviewArrangement(r.subjectId, r.head, r.forward, r.pages);
        r.sheet.move(r.arrangement.sheets);
        r.swimmer.move(r.arrangement.swimmer);
        continue;
      }
      const { sheet, swimmer } = this.cockpit();
      const pages = sheet.load(a.subjectId);
      const arrangement = reviewArrangement(a.subjectId, head, forward, pages);
      this.reviews.push({
        subjectId: a.subjectId,
        arrangement,
        head,
        forward,
        pages,
        sheet,
        swimmer,
      });
      sheet.place(arrangement.sheets);
      swimmer.show(a.subjectId, arrangement.swimmer);
      out.push(a.subjectId);
    }
    this.rebuildStatics();
    this.rebuildBodies();
    this.assignLods(undefined, true);
    this.console.invalidate();
    return { out, back };
  }

  /** One patient (or every patient) back to the formation; their cockpits fold away. */
  sendBack(subjectId?: string): string[] {
    const gone = this.reviews.filter((r) => subjectId === undefined || r.subjectId === subjectId);
    if (!gone.length) return [];
    for (const r of gone) {
      r.sheet.hide();
      r.swimmer.hide();
      this.reviews.splice(this.reviews.indexOf(r), 1);
      if (this.drag?.subjectId === r.subjectId) this.drag = undefined;
    }
    this.rebuildStatics();
    this.assignLods(undefined, true);
    this.console.invalidate();
    return gone.map((r) => r.subjectId);
  }

  /* ---------- State ---------- */

  /** Room filters re-pack the formation with the subjects that pass (the review subject stays). */
  setFilters(filters: DashboardFilters): void {
    this.filters = filters;
    this.relayout();
    if (this.timelineSubject && !this.slotById.has(this.timelineSubject))
      this.setTimeline(undefined);
  }

  private relayout(): void {
    // Summoned patients stand in their cockpits; the formation keeps everyone else who passes.
    const shown = this.view.subjects({
      ...this.filters,
      subjectId: undefined,
      subjectIds: undefined,
    });
    this.slots = layoutWard(shown);
    this.slotById.clear();
    for (const s of this.slots) this.slotById.set(s.subjectId, s);
    this.labelMesh.geometry.dispose();
    this.labelMesh.geometry = this.buildLabelGeometry();
    this.labelMesh.geometry.computeBoundingSphere();
    this.redrawAtlas();
    this.rebuildStatics();
    this.assignLods(undefined, true);
    this.console.invalidate();
  }

  /** Subjects whose profile is open: lod0 body and a teal plinth. */
  setOpen(ids: string[]): void {
    this.open = new Set(ids);
    this.rebuildStatics();
    this.assignLods(undefined, true);
  }

  cycleOverlays(): OverlayMode {
    this.overlays =
      OVERLAY_CYCLE[(OVERLAY_CYCLE.indexOf(this.overlays) + 1) % OVERLAY_CYCLE.length]!;
    this.rebuildOverlays();
    this.console.invalidate();
    return this.overlays;
  }

  setOverlays(mode: OverlayMode): void {
    if (mode === this.overlays) return;
    this.overlays = mode;
    this.rebuildOverlays();
    this.console.invalidate();
  }

  overlayLabel(): string {
    return {
      off: 'Overlays: off',
      labs: 'Overlays: labs',
      aes: 'Overlays: AEs',
      both: 'Overlays: labs + AEs',
    }[this.overlays];
  }

  get timeline(): string | undefined {
    return this.timelineSubject;
  }

  /** Lays the visit discs along the aisle for one subject, or clears them. */
  setTimeline(subjectId: string | undefined): void {
    if (subjectId === this.timelineSubject) return;
    this.timelineSubject = subjectId;
    this.lastStep = undefined;
    this.stepTip.hide();
    this.rebuildTimeline();
    this.console.invalidate();
  }

  timelineNodeList(): TimelineNode[] {
    return this.timelineNodesList;
  }

  setMiniatures(items: Miniature[]): void {
    this.miniatures = items;
    this.rebuildStatics();
    this.rebuildBodies();
  }

  /** Data changed (queries): refresh what shows it. */
  refreshData(): void {
    // A record may now need another page: lay each cockpit out again around its anchor.
    for (const r of this.reviews) {
      r.pages = r.sheet.load(r.subjectId);
      r.arrangement = reviewArrangement(r.subjectId, r.head, r.forward, r.pages);
      r.sheet.place(r.arrangement.sheets);
      r.swimmer.refresh();
    }
    if (this.reviews.length) this.rebuildStatics();
  }

  /* ---------- Per frame ---------- */

  update(dt: number, head: THREE.Vector3, feet: { x: number; z: number }): void {
    this.time += dt;
    this.material.uniforms.uTime.value = this.time;
    if (this.pulsing.length) {
      const k = 0.55 + 0.45 * Math.sin(this.time * 5);
      const c = new THREE.Color(PALETTE.crimson).lerp(new THREE.Color(0xffffff), k * 0.5);
      for (const i of this.pulsing) this.rings.setColorAt(i, c);
      this.rings.instanceColor!.needsUpdate = true;
    }
    const now = performance.now();
    if (this.ready && now - this.lastLodAt > LOD_INTERVAL_MS) {
      this.lastLodAt = now;
      this.assignLods(head, false);
    }
    this.lastHead.copy(head);
    // The review panels stand where the how-to sign is; it steps aside while a patient is out.
    this.hint.visible = this.reviews.length === 0;
    this.pollGrip(now);
    if (this.timelineSubject) {
      const near = nearestTimelineNode(this.timelineNodesList, feet);
      const key = near?.visitId;
      if (key !== this.lastStep) {
        this.lastStep = key;
        this.handlers.timelineStep(this.timelineSubject, key);
        if (near) {
          const lines = this.visitSummary(near.visitId);
          this.stepTip.show(
            lines,
            new THREE.Vector3(near.x, 1.25, near.z),
            new THREE.Vector3(head.x, 1.25, head.z),
          );
        } else this.stepTip.hide();
      }
    }
    this.console.update();
    for (const c of this.cockpitPool) {
      c.sheet.update();
      c.swimmer.update();
    }
  }

  /**
   * Grip (controllers) or G (desktop) while pointing at a mannequin: a mannequin in the
   * formation is pulled out for review; holding the grip drags the pulled-out mannequin
   * wherever the ray points; two quick grips on it send it back to its plinth.
   */
  private pollGrip(now = performance.now()): void {
    for (const p of this.input.activePointers()) {
      const squeezing = p.kind === 'mouse' ? this.input.keysDown.has('KeyG') : p.squeezing;
      const was = this.wasSqueezing.get(p.id) ?? false;
      this.wasSqueezing.set(p.id, squeezing);
      if (this.drag?.pointerId === p.id) {
        if (squeezing) this.dragWith(p, now);
        else {
          const id = this.drag.subjectId;
          this.drag = undefined;
          const slot = this.reviewOf(id)?.arrangement.slot;
          if (slot) this.moveReviewTo(slot.x, slot.z, now, true, id);
        }
        continue;
      }
      if (!squeezing || was) continue;
      const subject = this.hoverSubject.get(p.id);
      if (!subject) continue;
      const double =
        this.lastGrip?.subjectId === subject && now - this.lastGrip.at < DOUBLE_GRIP_MS;
      this.lastGrip = { subjectId: subject, at: now };
      if (this.isPulled(subject)) {
        if (double) {
          this.lastGrip = undefined;
          this.handlers.grab(subject);
        } else this.startDrag(p, subject);
      } else {
        this.handlers.grab(subject);
        if (this.isPulled(subject)) this.startDrag(p, subject);
      }
    }
  }

  private startDrag(p: Pointer, subjectId: string): void {
    const slot = this.reviewOf(subjectId)?.arrangement.slot;
    if (!slot) return;
    const distance = Math.hypot(slot.x - p.ray.origin.x, slot.z - p.ray.origin.z);
    this.drag = { pointerId: p.id, distance: THREE.MathUtils.clamp(distance, 0.8, 6), subjectId };
  }

  /** Keeps the dragged mannequin at its grab distance along the ray, on the floor. */
  private dragWith(p: Pointer, now: number): void {
    if (!this.drag) return;
    const dx = p.ray.direction.x;
    const dz = p.ray.direction.z;
    const len = Math.hypot(dx, dz);
    if (len < 0.15) return; // pointing straight down: hold still
    this.moveReviewTo(
      p.ray.origin.x + (dx / len) * this.drag.distance,
      p.ray.origin.z + (dz / len) * this.drag.distance,
      now,
      false,
      this.drag.subjectId,
    );
  }

  /** True while a pulled-out mannequin is being dragged. */
  get dragging(): boolean {
    return !!this.drag;
  }

  /**
   * Moves a summoned patient's whole cockpit as one piece: the mannequin goes to (x, z) and
   * turns to face the viewer, and its record pages, swimmer plot and profile turn and move with
   * it, keeping their places around it. Panels move without redrawing; the plinth, ring, spine
   * and chest number rebuild at about 30 Hz while dragging.
   */
  moveReviewTo(
    x: number,
    z: number,
    now = performance.now(),
    force = false,
    subjectId = this.pulled,
  ): void {
    const r = this.reviewOf(subjectId);
    if (!r) return;
    const old = r.arrangement.slot;
    // Anywhere on the floor (the hall, the studio, the ward room), clear of the walls.
    const { x: nx, z: nz } = clampToPolygon({ x, z }, this.floor, 0.6);
    const turn = Math.atan2(this.lastHead.x - nx, this.lastHead.z - nz) - old.yaw;
    // Rigid move: the anchor turns about the patient by the same angle, then travels with it.
    const c = Math.cos(turn);
    const s = Math.sin(turn);
    const rx = r.head.x - old.x;
    const rz = r.head.z - old.z;
    r.head.set(nx + rx * c + rz * s, r.head.y, nz - rx * s + rz * c);
    const fx = r.forward.x;
    const fz = r.forward.z;
    r.forward.set(fx * c + fz * s, 0, -fx * s + fz * c);
    r.arrangement = reviewArrangement(r.subjectId, r.head, r.forward, r.pages);
    r.sheet.move(r.arrangement.sheets);
    r.swimmer.move(r.arrangement.swimmer);
    this.handlers.reviewMoved(r.subjectId);
    if (!force && now - this.lastDragBuild < 33) return;
    this.lastDragBuild = now;
    this.rebuildStatics();
    this.rebuildBodies();
  }

  /** Dev hook: the console's region ids (filters and controls). */
  consoleRegions(): string[] {
    this.console.update();
    return this.console.allRegions().map((r) => r.id);
  }

  /** What a visit disc says when stepped on. */
  visitSummary(visitId: string): string[] {
    const v = this.view.visit(visitId);
    if (!v) return [visitId];
    const forms = this.view.file.forms.filter((f) => f.visitId === v.id);
    const entered = forms.filter((f) => f.status !== 'Not Started').length;
    const missing = forms.reduce((n, f) => n + f.missingFields.length, 0);
    const labs = this.view.file.labs.filter((l) => l.visitId === v.id);
    const worst = labs.reduce((g, l) => Math.max(g, l.grade), 0);
    const aes = this.view.file.adverseEvents.filter(
      (a) =>
        a.subjectId === v.subjectId && a.onsetDate >= v.windowStart && a.onsetDate <= v.windowEnd,
    );
    return [
      `${v.visitName} · ${v.actualDate ?? v.status.toLowerCase()}`,
      `${v.timing}${v.deviationDays ? ` (${v.deviationDays > 0 ? '+' : ''}${v.deviationDays} d)` : ''}${v.missedReason ? ` · ${v.missedReason}` : ''}`,
      forms.length
        ? `${entered}/${forms.length} pages entered${missing ? ` · ${missing} fields missing` : ''}`
        : 'no pages at this visit',
      `${labs.length ? `labs worst grade ${worst}` : 'no labs'} · ${aes.length} AE${aes.length === 1 ? '' : 's'} in window`,
    ];
  }

  /* ---------- Bodies and LODs ---------- */

  private assignLods(head: THREE.Vector3 | undefined, force: boolean): void {
    if (!this.ready) return;
    let changed = force;
    const nearest = head
      ? [...this.slots]
          .sort(
            (a, b) =>
              Math.hypot(a.x - head.x, a.z - head.z) - Math.hypot(b.x - head.x, b.z - head.z),
          )
          .slice(0, 2)
          .map((s) => s.subjectId)
      : [];
    const next = new Map<string, Lod>();
    for (const slot of this.slots) {
      let lod: Lod;
      if (
        this.open.has(slot.subjectId) ||
        this.isPulled(slot.subjectId) ||
        nearest.includes(slot.subjectId)
      )
        lod = 'lod0';
      else if (!head) {
        const prev = this.lods.get(slot.subjectId) ?? 'lod2';
        lod = prev === 'lod0' ? 'lod1' : prev;
      } else lod = Math.hypot(slot.x - head.x, slot.z - head.z) < NEAR_M ? 'lod1' : 'lod2';
      next.set(slot.subjectId, lod);
      if (this.lods.get(slot.subjectId) !== lod) changed = true;
    }
    if (next.size !== this.lods.size) changed = true;
    this.lods = next;
    if (changed) this.rebuildBodies();
  }

  private rebuildBodies(): void {
    if (!this.ready) return;
    const counts = new Map<THREE.InstancedMesh, number>();
    for (const m of Object.values(this.bodies)) {
      if (!m) continue;
      counts.set(m, 0);
      this.bodyEntries.set(m, []);
    }
    const dummy = new THREE.Object3D();
    const colour = new THREE.Color();
    const lod0 = { M: 0, F: 0 };
    const chest = this.chestMesh.geometry.getAttribute('position') as THREE.BufferAttribute;
    (chest.array as Float32Array).fill(0);
    this.chestCount = 0;
    const place = (mesh: THREE.InstancedMesh, entry: BodyEntry) => {
      const i = counts.get(mesh) ?? 0;
      if (i >= mesh.instanceMatrix.count) return;
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      mesh.setColorAt(i, colour.set(skinTone(entry.subjectId)));
      this.bodyEntries.get(mesh)!.push(entry);
      counts.set(mesh, i + 1);
    };
    const stand = (s: Subject, slot: WardSlot, lodWanted: Lod) => {
      let lod = lodWanted;
      if (lod === 'lod0' && lod0[s.sex]++ >= LOD_CAP.lod0) lod = 'lod1';
      const mesh = this.bodies[`${s.sex}-${lod}`];
      if (!mesh) return;
      const sc = bodyScale(s);
      dummy.position.set(slot.x, slot.y + WARD.plinthHeight, slot.z);
      dummy.rotation.set(0, slot.yaw, 0);
      dummy.scale.set(sc.x, sc.y, sc.z);
      place(mesh, { subjectId: s.id });
      this.placeChest(chest, s.id, slot, sc);
    };
    for (const formation of this.slots) {
      const s = this.subjectById.get(formation.subjectId);
      if (!s) continue;
      stand(s, this.effectiveSlot(s.id) ?? formation, this.lods.get(s.id) ?? 'lod2');
    }
    // A review subject the room filter hides from the formation still stands in its cockpit.
    for (const r of this.reviews) {
      if (this.slotById.has(r.subjectId)) continue;
      const s = this.subjectById.get(r.subjectId);
      if (s) stand(s, r.arrangement.slot, 'lod0');
    }
    for (const m of this.miniatures) {
      const s = this.subjectById.get(m.subjectId);
      const mesh = s && this.bodies[`${s.sex}-lod1`];
      if (!s || !mesh) continue;
      const sc = bodyScale(s);
      dummy.position.set(m.position.x, m.position.y + WARD.plinthHeight * MINI_SCALE, m.position.z);
      dummy.rotation.set(0, m.yaw, 0);
      dummy.scale.set(sc.x * MINI_SCALE, sc.y * MINI_SCALE, sc.z * MINI_SCALE);
      place(mesh, { subjectId: m.subjectId, miniature: m.id });
    }
    for (const [mesh, n] of counts) finish(mesh, n);
    chest.needsUpdate = true;
    this.chestMesh.geometry.computeBoundingSphere();
    this.hovered = undefined;
  }

  /** One quad per subject; those not standing stay collapsed at the origin. */
  private buildChestGeometry(): THREE.BufferGeometry {
    const subjects = this.view.file.subjects.slice(0, CHEST_ROWS * ATLAS_COLS);
    const pos = new Float32Array(subjects.length * 12);
    const uv = new Float32Array(subjects.length * 8);
    const index: number[] = [];
    const atlasW = CELL_W * ATLAS_COLS;
    subjects.forEach((s, i) => {
      this.chestIndex.set(s.id, i);
      const col = i % ATLAS_COLS;
      const row = Math.floor(i / ATLAS_COLS);
      const u0 = (col * CHEST_CELL.w) / atlasW;
      const u1 = ((col + 1) * CHEST_CELL.w) / atlasW;
      const top = CHEST_Y0 + row * CHEST_CELL.h;
      const v1 = 1 - top / ATLAS_H;
      const v0 = 1 - (top + CHEST_CELL.h) / ATLAS_H;
      // Corners in PlaneGeometry order: top left, top right, bottom left, bottom right.
      uv.set([u0, v1, u1, v1, u0, v0, u1, v0], i * 8);
      const b = i * 4;
      index.push(b, b + 2, b + 1, b + 2, b + 3, b + 1);
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    g.setIndex(index);
    return g;
  }

  /** The subject number on the chest, facing the way the body faces. */
  private placeChest(
    attr: THREE.BufferAttribute,
    subjectId: string,
    slot: WardSlot,
    sc: { x: number; y: number; z: number },
  ): void {
    const i = this.chestIndex.get(subjectId);
    if (i === undefined) return;
    const f = forwardOf(slot);
    // The body's local +x after the yaw: the viewer's right when facing it.
    const rx = Math.cos(slot.yaw);
    const rz = -Math.sin(slot.yaw);
    const d = CHEST_AT.depth * sc.z + CHEST_AT.gap;
    const cx = slot.x + f.x * d;
    const cy = slot.y + WARD.plinthHeight + CHEST_AT.y * sc.y;
    const cz = slot.z + f.z * d;
    const hw = CHEST_W / 2;
    const hh = CHEST_H / 2;
    const a = attr.array as Float32Array;
    const corner = (k: number, sx: number, sy: number) => {
      a[(i * 4 + k) * 3] = cx + rx * hw * sx;
      a[(i * 4 + k) * 3 + 1] = cy + hh * sy;
      a[(i * 4 + k) * 3 + 2] = cz + rz * hw * sx;
    };
    corner(0, -1, 1);
    corner(1, 1, 1);
    corner(2, -1, -1);
    corner(3, 1, -1);
    this.chestCount++;
  }

  /* ---------- Plinths, rings, spines ---------- */

  private rebuildStatics(): void {
    const dummy = new THREE.Object3D();
    const colour = new THREE.Color();
    this.pulsing.length = 0;
    this.plinthSubjects = [];
    let p = 0;
    let r = 0;
    let rod = 0;
    let n = 0;
    let h = 0;
    this.nodeEntries.length = 0;
    this.hollowEntries.length = 0;
    const schedule = this.view.file.study.visitSchedule.length;
    const visitsBySubject = new Map<string, Visit[]>();
    for (const v of this.view.file.visits) {
      const list = visitsBySubject.get(v.subjectId) ?? [];
      list.push(v);
      visitsBySubject.set(v.subjectId, list);
    }
    const plinth = (slot: WardSlot, colourHex: string, subjectId: string, scale = 1) => {
      if (p >= this.plinths.instanceMatrix.count) return;
      const height = (slot.y + WARD.plinthHeight) * scale;
      dummy.position.set(slot.x, height / 2, slot.z);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(scale, height, scale);
      dummy.updateMatrix();
      this.plinths.setMatrixAt(p, dummy.matrix);
      this.plinths.setColorAt(p, colour.set(colourHex));
      this.plinthSubjects[p] = subjectId;
      p++;
    };
    const ring = (slot: WardSlot, ringHex: string, pulse: boolean) => {
      if (r >= this.rings.instanceMatrix.count) return;
      dummy.position.set(slot.x, slot.y + WARD.plinthHeight + 0.01, slot.z);
      dummy.rotation.set(Math.PI / 2, 0, 0);
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      this.rings.setMatrixAt(r, dummy.matrix);
      this.rings.setColorAt(r, colour.set(ringHex));
      if (pulse) this.pulsing.push(r);
      r++;
    };
    const spine = (s: Subject, slot: WardSlot) => {
      if (rod >= this.rods.instanceMatrix.count) return;
      // Beside the body on the viewer's left, where the swimmer plot is too.
      const { x: bx, z: bz } = spineBase(
        slot,
        this.reviews.some((r) => r.arrangement.slot === slot),
      );
      const base = slot.y + WARD.plinthHeight;
      dummy.position.set(bx, base + (SPINE.yMin + SPINE.yMax) / 2, bz);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      this.rods.setMatrixAt(rod++, dummy.matrix);
      const visits = (visitsBySubject.get(s.id) ?? []).sort(
        (a, b) => this.view.visitIndex(a.visitCode) - this.view.visitIndex(b.visitCode),
      );
      for (const node of spineNodes(visits, schedule)) {
        const v = visits.find((x) => x.id === node.visitId)!;
        const entry: OverlayEntry = {
          target: { kind: 'visit', subjectId: s.id, visitId: v.id },
          tooltip: [
            `${s.id} · ${v.visitName}`,
            `${v.status} · ${v.timing}${v.actualDate ? ` · ${v.actualDate}` : ''}`,
            'Select to open the visit',
          ],
        };
        if (node.kind === 'hollow') {
          if (h >= CAP.hollow) continue;
          dummy.position.set(bx, base + node.y, bz);
          dummy.rotation.set(0, slot.yaw, 0);
          dummy.scale.setScalar(1);
          dummy.updateMatrix();
          this.hollow.setMatrixAt(h, dummy.matrix);
          this.hollow.setColorAt(h, colour.set(node.colour));
          this.hollowEntries[h++] = entry;
        } else {
          if (n >= CAP.nodes) continue;
          dummy.position.set(bx, base + node.y, bz);
          dummy.rotation.set(0, 0, 0);
          dummy.scale.setScalar(nodeRadius(node));
          dummy.updateMatrix();
          this.nodes.setMatrixAt(n, dummy.matrix);
          this.nodes.setColorAt(n, colour.set(node.colour));
          this.nodeEntries[n++] = entry;
        }
      }
    };
    for (const formation of this.slots) {
      const s = this.subjectById.get(formation.subjectId);
      if (!s) continue;
      const pulled = this.isPulled(s.id);
      const open = this.open.has(s.id);
      plinth(formation, pulled ? PLINTH_EMPTY : open ? PLINTH_OPEN : PLINTH_COLOUR, s.id);
      const rc = ringColour(s);
      if (pulled) ring(formation, PLINTH_EMPTY, false);
      else {
        ring(formation, rc.colour, rc.pulse);
        spine(s, formation);
      }
    }
    for (const r of this.reviews) {
      const s = this.subjectById.get(r.subjectId);
      if (!s) continue;
      const slot = r.arrangement.slot;
      plinth(slot, PLINTH_OPEN, s.id);
      const rc = ringColour(s);
      ring(slot, rc.colour, rc.pulse);
      spine(s, slot);
    }
    for (const m of this.miniatures) {
      const slot: WardSlot = {
        subjectId: m.subjectId,
        x: m.position.x,
        z: m.position.z,
        y: m.position.y / MINI_SCALE,
        yaw: m.yaw,
        row: -1,
        column: -1,
      };
      plinth(slot, PLINTH_OPEN, m.subjectId, MINI_SCALE);
    }
    finish(this.plinths, p);
    finish(this.rings, r);
    finish(this.rods, rod);
    finish(this.nodes, n);
    finish(this.hollow, h);
    this.rebuildOverlays();
  }

  /* ---------- Body overlays ---------- */

  private rebuildOverlays(): void {
    const dummy = new THREE.Object3D();
    const colour = new THREE.Color();
    let o = 0;
    let a = 0;
    this.organEntries.length = 0;
    this.aeEntries.length = 0;
    const showLabs = this.overlays === 'labs' || this.overlays === 'both';
    const showAes = this.overlays === 'aes' || this.overlays === 'both';
    if (showLabs || showAes) {
      const labsBySubject = new Map<string, typeof this.view.file.labs>();
      const aesBySubject = new Map<string, typeof this.view.file.adverseEvents>();
      if (showLabs)
        for (const l of this.view.file.labs) {
          if (l.grade < 1) continue;
          const list = labsBySubject.get(l.subjectId) ?? [];
          list.push(l);
          labsBySubject.set(l.subjectId, list);
        }
      if (showAes)
        for (const ae of this.view.file.adverseEvents) {
          const list = aesBySubject.get(ae.subjectId) ?? [];
          list.push(ae);
          aesBySubject.set(ae.subjectId, list);
        }
      const ids = this.slots.map((s) => s.subjectId);
      for (const r of this.reviews) if (!ids.includes(r.subjectId)) ids.push(r.subjectId);
      for (const id of ids) {
        const s = this.subjectById.get(id);
        const slot = this.effectiveSlot(id);
        if (!s || !slot) continue;
        const sc = bodyScale(s);
        const base = slot.y + WARD.plinthHeight;
        if (showLabs) {
          // The worst result per organ, latest among ties; a long QTcF lights the heart.
          const worst = new Map<Organ, OrganHit>();
          const consider = (organ: Organ, hit: OrganHit) => {
            const cur = worst.get(organ);
            if (!cur || hit.grade > cur.grade || (hit.grade === cur.grade && hit.date >= cur.date))
              worst.set(organ, hit);
          };
          for (const l of labsBySubject.get(s.id) ?? []) {
            const organ = ANALYTE_ORGAN[l.analyte];
            if (organ)
              consider(organ, {
                kind: 'lab',
                id: l.id,
                analyte: l.analyte,
                value: `${l.value} ${l.unit}`,
                grade: l.grade,
                visitCode: l.visitCode,
                date: l.date,
              });
          }
          for (const e of this.view.file.ecgs)
            if (e.subjectId === s.id && e.qtcfMs > 450)
              consider('Heart', {
                kind: 'ecg',
                id: e.id,
                analyte: 'QTcF',
                value: `${e.qtcfMs} ms`,
                grade: e.qtcfMs > 500 ? 3 : e.qtcfMs > 480 ? 2 : 1,
                visitCode: e.visitCode,
                date: e.date,
              });
          for (const [organ, l] of worst) {
            if (o >= CAP.organs) break;
            const pt = toSlot(ORGAN_ANCHOR[organ], slot, sc);
            dummy.position.set(pt.x, pt.y + base, pt.z);
            dummy.rotation.set(0, 0, 0);
            dummy.scale.setScalar(0.035 + 0.012 * l.grade);
            dummy.updateMatrix();
            this.organs.setMatrixAt(o, dummy.matrix);
            this.organs.setColorAt(o, colour.set(gradeColour(l.grade)));
            this.organEntries[o++] = {
              target:
                l.kind === 'ecg'
                  ? { kind: 'ecg', subjectId: s.id, ecgId: l.id }
                  : { kind: 'lab', subjectId: s.id, labId: l.id, analyte: l.analyte },
              tooltip: [
                `${s.id} · ${organ}`,
                `${l.analyte} grade ${l.grade} · ${l.value} at ${l.visitCode}`,
                'Select to open the result',
              ],
            };
          }
        }
        if (showAes) {
          const stack = new Map<string, number>();
          for (const ae of aesBySubject.get(s.id) ?? []) {
            if (a >= CAP.aes) break;
            const k = stack.get(ae.bodyRegion) ?? 0;
            stack.set(ae.bodyRegion, k + 1);
            const anchor = REGION_ANCHOR[ae.bodyRegion];
            const pt = toSlot({ x: anchor.x, y: anchor.y, z: anchor.z + 0.02 }, slot, sc);
            dummy.position.set(pt.x, pt.y + base + k * 0.05, pt.z);
            dummy.rotation.set(0, this.time, 0);
            dummy.scale.setScalar(ae.serious ? 0.032 : 0.024);
            dummy.updateMatrix();
            this.aeMarkers.setMatrixAt(a, dummy.matrix);
            this.aeMarkers.setColorAt(
              a,
              colour.set(ae.serious ? PALETTE.crimson : gradeColour(ae.grade)),
            );
            this.aeEntries[a++] = {
              target: { kind: 'ae', subjectId: s.id, aeId: ae.id },
              tooltip: [
                `${s.id} · ${ae.term}`,
                `${ae.serious ? 'SAE' : 'AE'} grade ${ae.grade} · ${ae.bodyRegion} · onset ${ae.onsetDate}`,
                'Select to open the event',
              ],
            };
          }
        }
      }
    }
    finish(this.organs, o);
    finish(this.aeMarkers, a);
  }

  /* ---------- Walkable timeline ---------- */

  private rebuildTimeline(): void {
    const dummy = new THREE.Object3D();
    const colour = new THREE.Color();
    this.discEntries = [];
    this.timelineNodesList = [];
    let d = 0;
    const id = this.timelineSubject;
    const slot = id ? this.slotById.get(id) : undefined;
    const parts: THREE.BufferGeometry[] = [];
    if (id && slot) {
      const visits = this.view.file.visits
        .filter((v) => v.subjectId === id)
        .sort((a, b) => this.view.visitIndex(a.visitCode) - this.view.visitIndex(b.visitCode))
        .slice(0, CAP.discs);
      const nodes = timelineNodes(visits);
      const kinds = spineNodes(visits, this.view.file.study.visitSchedule.length);
      this.timelineNodesList = nodes;
      nodes.forEach((node, i) => {
        const v = visits[i]!;
        dummy.position.set(node.x, 0.007, node.z);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.setScalar(1);
        dummy.updateMatrix();
        this.discs.setMatrixAt(d, dummy.matrix);
        this.discs.setColorAt(d, colour.set(kinds[i]!.colour));
        this.discEntries[d++] = {
          node,
          target: { kind: 'visit', subjectId: id, visitId: v.id },
          tooltip: [
            `${v.visitName} · ${v.actualDate ?? v.status.toLowerCase()}`,
            v.timing,
            'Step on it, or select',
          ],
        };
        // Flat on the floor, reading for someone looking down the aisle (+z): up is +z.
        const cell = SUBJECT_ROWS * ATLAS_COLS + i;
        const g = new THREE.PlaneGeometry(LABEL_W * 0.7, LABEL_H * 0.7);
        setCellUv(g, cell);
        g.rotateX(-Math.PI / 2);
        g.rotateY(Math.atan2(-WARD.forward.x, -WARD.forward.z));
        const ahead = i % 2 === 0 ? 0.26 : 0.44;
        g.translate(node.x + WARD.forward.x * ahead, 0.004, node.z + WARD.forward.z * ahead);
        parts.push(g);
      });
    }
    finish(this.discs, d);
    this.discLabels.geometry.dispose();
    this.discLabels.geometry = parts.length
      ? mergeGeometries(parts, false)
      : new THREE.BufferGeometry();
    this.discLabels.geometry.computeBoundingSphere();
    this.redrawAtlas();
  }

  /* ---------- Labels ---------- */

  /** One tilted quad in front of every plinth, UV-mapped to its atlas cell. */
  private buildLabelGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    this.slots.forEach((slot, i) => {
      if (i >= SUBJECT_ROWS * ATLAS_COLS) return;
      const g = new THREE.PlaneGeometry(LABEL_W, LABEL_H);
      setCellUv(g, i);
      const m = new THREE.Matrix4()
        .makeRotationY(slot.yaw)
        .multiply(new THREE.Matrix4().makeRotationX(-0.85))
        .setPosition(
          slot.x + forwardOf(slot).x * (WARD.plinthRadius + 0.12),
          slot.y + 0.06,
          slot.z + forwardOf(slot).z * (WARD.plinthRadius + 0.12),
        );
      g.applyMatrix4(m);
      parts.push(g);
    });
    return parts.length ? mergeGeometries(parts, false) : new THREE.BufferGeometry();
  }

  private redrawAtlas(): void {
    const ctx = this.atlas.getContext('2d')!;
    ctx.clearRect(0, 0, this.atlas.width, this.atlas.height);
    const cell = (index: number, draw: () => void) => {
      const col = index % ATLAS_COLS;
      const row = Math.floor(index / ATLAS_COLS);
      ctx.save();
      ctx.setTransform(
        ATLAS_SCALE,
        0,
        0,
        ATLAS_SCALE,
        col * CELL_W * ATLAS_SCALE,
        row * CELL_H * ATLAS_SCALE,
      );
      draw();
      ctx.restore();
    };
    this.slots.forEach((slot, i) => {
      const s = this.subjectById.get(slot.subjectId);
      if (!s || i >= SUBJECT_ROWS * ATLAS_COLS) return;
      cell(i, () => {
        fillRoundRect(ctx, { x: 2, y: 2, w: CELL_W - 4, h: CELL_H - 4 }, 10, PALETTE.surface);
        ctx.fillStyle = STATUS_COLOUR[s.status];
        ctx.fillRect(6, 10, 5, CELL_H - 20);
        text(ctx, s.id, 18, 26, { size: 21, weight: 800 });
        ctx.font = font(12, 500);
        text(
          ctx,
          truncate(ctx, `Site ${s.siteId} · ${s.arm ?? 'not randomised'}`, CELL_W - 26),
          18,
          45,
          { size: 12, colour: PALETTE.muted },
        );
        text(ctx, s.status, 18, 62, { size: 12, weight: 600, colour: STATUS_COLOUR[s.status] });
      });
    });
    this.view.file.subjects.forEach((s) => {
      const i = this.chestIndex.get(s.id);
      if (i === undefined) return;
      ctx.save();
      ctx.setTransform(
        ATLAS_SCALE,
        0,
        0,
        ATLAS_SCALE,
        (i % ATLAS_COLS) * CHEST_CELL.w * ATLAS_SCALE,
        (CHEST_Y0 + Math.floor(i / ATLAS_COLS) * CHEST_CELL.h) * ATLAS_SCALE,
      );
      fillRoundRect(
        ctx,
        { x: 2, y: 2, w: CHEST_CELL.w - 4, h: CHEST_CELL.h - 4 },
        10,
        PALETTE.surface,
      );
      ctx.fillStyle = STATUS_COLOUR[s.status];
      ctx.fillRect(9, 10, 5, CHEST_CELL.h - 20);
      const size = fitSize(ctx, s.id, CHEST_CELL.w - 36, 22, 12, 800);
      text(ctx, s.id, (CHEST_CELL.w + 14) / 2, CHEST_CELL.h / 2 + size * 0.48, {
        size,
        weight: 800,
        align: 'center',
      });
      ctx.restore();
    });
    const id = this.timelineSubject;
    if (id) {
      const visits = this.view.file.visits
        .filter((v) => v.subjectId === id)
        .sort((a, b) => this.view.visitIndex(a.visitCode) - this.view.visitIndex(b.visitCode))
        .slice(0, CAP.discs);
      visits.forEach((v, i) => {
        cell(SUBJECT_ROWS * ATLAS_COLS + i, () => {
          fillRoundRect(ctx, { x: 2, y: 2, w: CELL_W - 4, h: CELL_H - 4 }, 10, PALETTE.surface);
          text(ctx, v.visitName, 14, 30, { size: 22, weight: 700, maxWidth: CELL_W - 28 });
          text(ctx, `${v.actualDate ?? v.status} · ${v.timing}`, 14, 56, {
            size: 15,
            colour: v.status === 'Missed' ? PALETTE.crimson : PALETTE.muted,
            maxWidth: CELL_W - 28,
          });
        });
      });
    }
    this.atlasTexture.needsUpdate = true;
  }

  /* ---------- Console: patient filters, legend and controls at the front ---------- */

  private drawConsole(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    fillRoundRect(ctx, { x: 0, y: 0, w: W, h: H }, 18, PALETTE.surface);
    const total = this.view.file.subjects.length;
    const n = this.slots.length;
    text(ctx, `Patient ward · ${n === total ? n : `${n} of ${total}`} subjects`, 24, 40, {
      size: 26,
      weight: 700,
    });
    text(
      ctx,
      'Filter the ward; every dashboard follows. Grip a pulled-out patient twice to send them back.',
      24,
      64,
      { size: 13, colour: PALETTE.muted, maxWidth: W - 48 },
    );
    const f = this.filters;
    const CHIP_RIGHT = 860;
    let y = 84;
    const group = (
      label: string,
      key: keyof DashboardFilters,
      options: { value: string; label: string }[],
    ) => {
      text(ctx, label.toUpperCase(), 24, y + 21, { size: 12, weight: 700, colour: PALETTE.muted });
      let x = 104;
      const current = f[key] as string | undefined;
      const all = [{ value: '', label: 'All' }, ...options];
      for (const o of all) {
        const id = `f:${key}:${o.value}`;
        ctx.font = font(15, 600);
        const w = ctx.measureText(o.label).width + 20;
        if (x + w > CHIP_RIGHT) {
          x = 104;
          y += 34;
        }
        const active = o.value ? current === o.value : !current;
        const r = chip(ctx, o.label, x, y + 4, {
          active,
          hovered: panel.isHovered(id),
          h: 28,
          size: 15,
        });
        panel.region({
          id,
          ...r,
          tooltip: [
            o.value ? `${label}: ${o.label}` : `${label}: all`,
            'Filters the ward and every dashboard',
          ],
        });
        x += r.w + 6;
      }
      y += 38;
    };
    group(
      'Site',
      'siteId',
      this.view.file.sites.map((site) => ({ value: site.id, label: `${site.id} ${site.city}` })),
    );
    group(
      'Arm',
      'arm',
      this.view.arms().map((a) => ({ value: a, label: a })),
    );
    group('Sex', 'sex', [
      { value: 'F', label: 'Female' },
      { value: 'M', label: 'Male' },
    ]);
    group(
      'Status',
      'status',
      SUBJECT_STATUSES.map((st) => ({ value: st, label: st })),
    );
    group(
      'Flag',
      'flag',
      SUBJECT_FLAGS.map((fl) => ({ value: fl, label: fl })),
    );
    group(
      'Age',
      'ageBand',
      AGE_BANDS.map((a) => ({ value: a, label: a })),
    );
    group(
      'BMI',
      'bmiBucket',
      BMI_BUCKETS.map((b) => ({ value: b, label: b })),
    );
    // Legend in the right-hand column.
    y = 92;
    const legend = (
      title: string,
      items: { colour: string; label: string; hollow?: boolean }[],
      x: number,
      top: number,
      w: number,
    ) => {
      text(ctx, title.toUpperCase(), x, top, { size: 11, weight: 700, colour: PALETTE.muted });
      items.forEach((it, i) => {
        const iy = top + 20 + i * 19;
        ctx.beginPath();
        ctx.arc(x + 6, iy - 4, 5, 0, Math.PI * 2);
        if (it.hollow) {
          ctx.strokeStyle = it.colour;
          ctx.lineWidth = 2;
          ctx.stroke();
        } else {
          ctx.fillStyle = it.colour;
          ctx.fill();
        }
        ctx.font = font(12, 500);
        text(ctx, truncate(ctx, it.label, w - 20), x + 18, iy, { size: 12 });
      });
    };
    legend(
      'Ring: status',
      [
        { colour: PALETTE.teal, label: 'On treatment' },
        { colour: PALETTE.forest, label: 'Completed' },
        { colour: PALETTE.amber, label: 'Discontinued' },
        { colour: PALETTE.grey, label: 'Screen failed' },
        { colour: PALETTE.violet, label: 'Lost to follow-up' },
        { colour: PALETTE.navy, label: 'Screening / enrolled' },
        { colour: PALETTE.crimson, label: 'SAE in the last 30 days (pulses)' },
      ],
      890,
      y,
      390,
    );
    y += 20 + 7 * 19 + 14;
    legend(
      'Spine: visits',
      [
        { colour: PALETTE.teal, label: 'Completed in window' },
        { colour: PALETTE.crimson, label: 'Completed out of window' },
        { colour: PALETTE.muted, label: 'Missed', hollow: true },
        { colour: PALETTE.grey, label: 'Scheduled' },
      ],
      890,
      y,
      390,
    );
    y += 20 + 4 * 19 + 14;
    legend(
      'Overlays',
      [
        { colour: '#e8c26a', label: 'Grade 1 lab or AE' },
        { colour: PALETTE.amber, label: 'Grade 2' },
        { colour: PALETTE.crimson, label: 'Grade 3+ or serious' },
        { colour: PALETTE.teal, label: 'Liver: ALT, AST, bilirubin, ALP, albumin' },
        { colour: PALETTE.teal, label: 'Kidney: creatinine, Na, K · Heart: QTcF' },
        { colour: PALETTE.teal, label: 'Blood: haematology · Pancreas: glucose' },
        { colour: PALETTE.teal, label: 'AE diamonds on the body region' },
      ],
      890,
      y,
      390,
    );
    // Controls.
    const cy = H - 52;
    let x = 24;
    const button = (id: string, label: string, active: boolean, tip: string) => {
      const r = chip(ctx, label, x, cy, { active, hovered: panel.isHovered(id), h: 34, size: 14 });
      panel.region({ id, ...r, tooltip: [tip] });
      x += r.w + 10;
    };
    button('clear', 'Clear patient filters', false, 'Every patient back in the ward');
    button('overlays', this.overlayLabel(), this.overlays !== 'off', 'Cycle: off, labs, AEs, both');
    button(
      'timeline',
      this.timelineSubject ? `Timeline: ${this.timelineSubject} (clear)` : 'Timeline: none',
      !!this.timelineSubject,
      'Walk the timeline from a profile; this clears it',
    );
    if (this.reviews.length)
      button(
        'sendback',
        this.reviews.length === 1
          ? `Send ${this.reviews[0]!.subjectId} back`
          : `Send all ${this.reviews.length} back`,
        true,
        'Back to the formation; dashboards reset',
      );
  }

  private consoleSelect(region: Region): void {
    if (region.id === 'overlays') this.cycleOverlays();
    else if (region.id === 'timeline') this.setTimeline(undefined);
    else if (region.id === 'sendback' && this.reviews.length) this.handlers.sendBackAll();
    else if (region.id === 'clear')
      this.handlers.filter({
        siteId: undefined,
        arm: undefined,
        sex: undefined,
        status: undefined,
        flag: undefined,
        ageBand: undefined,
        bmiBucket: undefined,
      });
    else if (region.id.startsWith('f:')) {
      const [, key, ...rest] = region.id.split(':');
      const value = rest.join(':');
      this.handlers.filter({ [key as keyof DashboardFilters]: value || undefined });
    }
  }

  dispose(): void {
    this.console.detach(this.interactive);
    this.console.dispose();
    for (const c of this.cockpitPool) {
      c.sheet.dispose();
      c.swimmer.dispose();
    }
    for (const m of [
      ...Object.values(this.bodies).filter((x): x is THREE.InstancedMesh => !!x),
      this.plinths,
      this.rings,
      this.rods,
      this.nodes,
      this.hollow,
      this.organs,
      this.aeMarkers,
      this.discs,
    ]) {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
    this.labelMesh.geometry.dispose();
    this.discLabels.geometry.dispose();
    this.labelMaterial.dispose();
    this.atlasTexture.dispose();
  }
}

function nodeRadius(node: SpineNode): number {
  return node.kind === 'pending' ? SPINE.radius * 0.6 : SPINE.radius;
}

function setCellUv(g: THREE.PlaneGeometry, cell: number): void {
  const uv = g.getAttribute('uv') as THREE.BufferAttribute;
  const col = cell % ATLAS_COLS;
  const row = Math.floor(cell / ATLAS_COLS);
  for (let k = 0; k < uv.count; k++)
    uv.setXY(k, (col + uv.getX(k)) / ATLAS_COLS, 1 - ((row + 1 - uv.getY(k)) * CELL_H) / ATLAS_H);
}

/** Commits instance buffers and refreshes the bounding sphere rays are tested against. */
function finish(mesh: THREE.InstancedMesh, count: number): void {
  mesh.count = count;
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.computeBoundingSphere();
}
